import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { useNotifications } from 'app/application/application'
import { useConnections } from 'app/connections/application'
import { InstalledConnection } from 'app/connections/types'

import { Button, Modal } from 'components'

import { SettingsSection } from '../Section/Section'
import styles from './DangerZone.module.scss'

interface DangerZoneProps {
  connection: InstalledConnection
}

export const DangerZone = ({ connection }: DangerZoneProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation('connections')
  const { createNotification, te } = useNotifications()
  const { deleteConnection } = useConnections()
  const [isConfirming, setIsConfirming] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleDelete = async () => {
    setIsDeleting(true)
    const result = await deleteConnection(connection)

    if (result.ok) {
      createNotification({
        message: t('delete.success.message', {
          name: t(`available.${connection.name}.displayName`),
        }),
        title: t('delete.success.title'),
        type: 'success',
      })
      void navigate('/connections')
      return
    }

    createNotification({
      message: result.val.message,
      title: te(`http.${result.val.code}`),
      type: 'error',
    })
    setIsDeleting(false)
  }

  return (
    <>
      <SettingsSection
        contentClassName={styles.dangerCard}
        description={t('manage.dangerZone.description')}
        title={t('manage.dangerZone.title')}
        variant="danger"
      >
        <div>
          <h3>{t('manage.dangerZone.delete.title')}</h3>
          <p>{t('manage.dangerZone.delete.description')}</p>
        </div>
        <Button destructive onClick={() => setIsConfirming(true)}>
          {t('manage.dangerZone.delete.action')}
        </Button>
      </SettingsSection>

      <Modal
        actions={
          <>
            <Button
              disabled={isDeleting}
              onClick={() => setIsConfirming(false)}
              variant="secondary"
            >
              {t('delete.cancel')}
            </Button>
            <Button destructive loading={isDeleting} onClick={() => void handleDelete()}>
              {t('delete.confirm')}
            </Button>
          </>
        }
        onClose={() => !isDeleting && setIsConfirming(false)}
        onSubmit={() => void handleDelete()}
        open={isConfirming}
        title={t('delete.title')}
      >
        {t('delete.description', {
          connectionName: t(`available.${connection.name}.displayName`),
        })}
      </Modal>
    </>
  )
}
